// src/services/usageService.js
// ============================================================
// USAGE TRACKING & LIMITS - Free tier enforcement
// ============================================================
// Checks current usage of kegs, locations, sublocations per brewery
// Used to enforce: 2 kegs, 1 location, 2 sublocations for free tier

/**
 * Get current usage stats for a brewery
 * @param {string} breweryId - Brewery ID
 * @param {Array} kegs - All kegs in the app
 * @param {Array} locations - All locations in the app
 * @returns {Object} Usage stats { kegCount, locationCount, sublocationCount }
 */
export const getBarUsage = (breweryId, kegs = [], locations = []) => {
  // Count kegs for this brewery
  const kegCount = kegs.filter(k => k.brewery_id === breweryId).length;

  // Count locations and sublocations for this brewery
  const breweryLocations = locations.filter(loc => loc.brewery_id === breweryId);
  const locationCount = breweryLocations.length;
  const sublocationCount = breweryLocations.reduce((sum, loc) => 
    sum + (loc.sublocations?.length || 0), 0
  );

  return {
    kegCount,
    locationCount,
    sublocationCount
  };
};

/**
 * Check if brewery can add a new keg (free tier: max 2)
 * @param {string} breweryId - Brewery ID
 * @param {string} plan - Brewery plan ('free', 'pro', 'premium', 'exempt')
 * @param {Array} kegs - All kegs
 * @returns {Object} { allowed: boolean, message: string, limit: number, current: number }
 */
export const canAddKeg = (breweryId, plan, kegs = []) => {
  const usage = getBarUsage(breweryId, kegs, []);
  
  const limits = {
    free: 2,
    pro: 10,
    premium: Infinity,
    exempt: Infinity
  };

  const limit = limits[plan] || limits.free;
  const allowed = usage.kegCount < limit;
  const message = allowed 
    ? null 
    : `Free plan limited to ${limit} keg${limit !== 1 ? 's' : ''}. Upgrade to add more.`;

  return {
    allowed,
    message,
    limit,
    current: usage.kegCount
  };
};

/**
 * Check if brewery can add a new location (free tier: max 1)
 * @param {string} breweryId - Brewery ID
 * @param {string} plan - Brewery plan ('free', 'pro', 'premium', 'exempt')
 * @param {Array} locations - All locations
 * @returns {Object} { allowed: boolean, message: string, limit: number, current: number }
 */
export const canAddLocation = (breweryId, plan, locations = []) => {
  const usage = getBarUsage(breweryId, [], locations);
  
  const limits = {
    free: 1,
    pro: 5,
    premium: Infinity,
    exempt: Infinity
  };

  const limit = limits[plan] || limits.free;
  const allowed = usage.locationCount < limit;
  const message = allowed 
    ? null 
    : `Free plan limited to ${limit} location. Upgrade to add more.`;

  return {
    allowed,
    message,
    limit,
    current: usage.locationCount
  };
};

/**
 * Check if brewery can add a new sublocation (free tier: max 2)
 * @param {string} breweryId - Brewery ID
 * @param {string} plan - Brewery plan
 * @param {Array} locations - All locations
 * @returns {Object} { allowed: boolean, message: string, limit: number, current: number }
 */
export const canAddSublocation = (breweryId, plan, locations = []) => {
  const usage = getBarUsage(breweryId, [], locations);
  
  const limits = {
    free: 2,
    pro: 10,
    premium: Infinity,
    exempt: Infinity
  };

  const limit = limits[plan] || limits.free;
  const allowed = usage.sublocationCount < limit;
  const message = allowed 
    ? null 
    : `Free plan limited to ${limit} sublocation${limit !== 1 ? 's' : ''}. Upgrade to add more.`;

  return {
    allowed,
    message,
    limit,
    current: usage.sublocationCount
  };
};

/**
 * Get all usage limits for a plan
 * @param {string} plan - Bar plan
 * @returns {Object} All limits for the plan
 */
export const getPlanLimits = (plan) => {
  const plans = {
    free: {
      kegs: 2,
      locations: 1,
      sublocations: 2,
      members: 2,
      name: 'Free',
      price: '$0/month'
    },
    pro: {
      kegs: 10,
      locations: 5,
      sublocations: 10,
      members: 10,
      name: 'Pro',
      price: '$9.99/month'
    },
    premium: {
      kegs: Infinity,
      locations: Infinity,
      sublocations: Infinity,
      members: Infinity,
      name: 'Premium',
      price: '$29.99/month'
    },
    exempt: {
      kegs: Infinity,
      locations: Infinity,
      sublocations: Infinity,
      members: Infinity,
      name: 'Exempt',
      price: 'Custom'
    }
  };

  return plans[plan] || plans.free;
};
